import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
} from "@mui/material";
import {
  LinkedIn,
  Twitter,
  Instagram,
  Facebook,
  Email,
  Phone,
  LocationOn,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { gradients, transparentColors } from "../../utils/theme";

/**
 * Footer Component (Public Pages)
 *
 * Footer for public landing pages with links, contact info, and social media.
 * Features:
 * - Responsive grid layout
 * - Quick links and legal links
 * - Social media icons
 * - Contact information
 * - Brand information
 */
const Footer: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const scrollToSection = (sectionId: string) => {
    navigate("/");
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  return (
    <Box
      component="footer"
      sx={{
        background: "linear-gradient(135deg, #0d1642 0%, #1a237e 100%)",
        color: "white",
        pt: 6,
        pb: 3,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                fontSize: "1.75rem",
                fontWeight: 700,
                letterSpacing: "-0.5px",
              }}
            >
              <Box component="span" sx={{ color: "white" }}>
                Mall
              </Box>
              <Box
                component="span"
                sx={{
                  background: gradients.gold,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                In
              </Box>
              <Box component="span" sx={{ color: "white" }}>
                Hand
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                color: transparentColors.white80,
                fontSize: "0.9375rem",
                fontWeight: 500,
              }}
            >
              A product of{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "white" }}>
                SimplyTechiefy
              </Box>
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 2, color: transparentColors.white70, lineHeight: 1.8 }}
            >
              Your complete Digital Mall companion. Explore stores, unlock
              exclusive offers, discover events, and enjoy seamless parking -
              all through WhatsApp. Premium mall experience, simplified.
            </Typography>
            <Box sx={{ mt: 3, display: "flex", gap: 1 }}>
              <IconButton
                size="small"
                aria-label="Facebook"
                href="https://facebook.com/mallinhand"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  bgcolor: transparentColors.white10,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: gradients.gold,
                    color: "#1a237e",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Facebook />
              </IconButton>
              <IconButton
                size="small"
                aria-label="LinkedIn"
                href="https://linkedin.com/company/mallinhand"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  bgcolor: transparentColors.white10,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: gradients.gold,
                    color: "#1a237e",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <LinkedIn />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Twitter"
                href="https://twitter.com/mallinhand"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  bgcolor: transparentColors.white10,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: gradients.gold,
                    color: "#1a237e",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Twitter />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Instagram"
                href="https://instagram.com/mallinhand"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "white",
                  bgcolor: transparentColors.white10,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: gradients.gold,
                    color: "#1a237e",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Instagram />
              </IconButton>
            </Box>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} md={2}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}
            >
              Quick Links
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Link
                onClick={() => scrollToSection("hero")}
                sx={{
                  color: transparentColors.white70,
                  cursor: "pointer",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  "&:hover": { color: "white" },
                }}
              >
                Home
              </Link>
              <Link
                onClick={() => navigate("/explore")}
                sx={{
                  color: transparentColors.white70,
                  cursor: "pointer",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  "&:hover": { color: "white" },
                }}
              >
                Explore Malls
              </Link>
              <Link
                onClick={() => scrollToSection("features")}
                sx={{
                  color: transparentColors.white70,
                  cursor: "pointer",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  "&:hover": { color: "white" },
                }}
              >
                Features
              </Link>
              <Link
                onClick={() => scrollToSection("how-it-works")}
                sx={{
                  color: transparentColors.white70,
                  cursor: "pointer",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  "&:hover": { color: "white" },
                }}
              >
                How It Works
              </Link>
              <Link
                onClick={() => scrollToSection("contact")}
                sx={{
                  color: transparentColors.white70,
                  cursor: "pointer",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  "&:hover": { color: "white" },
                }}
              >
                Contact
              </Link>
            </Box>
          </Grid>

          {/* Features */}
          <Grid item xs={6} md={3}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}
            >
              Features
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                sx={{ color: transparentColors.white70, fontSize: "0.875rem" }}
              >
                Stores Directory
              </Typography>
              <Typography
                sx={{ color: transparentColors.white70, fontSize: "0.875rem" }}
              >
                Exclusive Offers
              </Typography>
              <Typography
                sx={{ color: transparentColors.white70, fontSize: "0.875rem" }}
              >
                Events Calendar
              </Typography>
              <Typography
                sx={{ color: transparentColors.white70, fontSize: "0.875rem" }}
              >
                Smart Parking
              </Typography>
              <Typography
                sx={{ color: transparentColors.white70, fontSize: "0.875rem" }}
              >
                WhatsApp Experience
              </Typography>
            </Box>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}
            >
              Contact Us
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Email
                  sx={{ fontSize: 18, color: transparentColors.white70 }}
                />
                <Link
                  href="mailto:support@mallinhand.com"
                  sx={{
                    color: transparentColors.white70,
                    fontSize: "0.875rem",
                    textDecoration: "none",
                    "&:hover": { color: "white" },
                  }}
                >
                  support@mallinhand.com
                </Link>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Phone
                  sx={{ fontSize: 18, color: transparentColors.white70 }}
                />
                <Link
                  href="tel:+919404880316"
                  sx={{
                    color: transparentColors.white70,
                    fontSize: "0.875rem",
                    textDecoration: "none",
                    "&:hover": { color: "white" },
                  }}
                >
                  +91 9404880316
                </Link>
              </Box>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <LocationOn
                  sx={{
                    fontSize: 18,
                    color: transparentColors.white70,
                    mt: 0.3,
                  }}
                />
                <Typography
                  sx={{
                    color: transparentColors.white70,
                    fontSize: "0.875rem",
                    lineHeight: 1.6,
                  }}
                >
                  Pune, Maharashtra, India
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider sx={{ my: 4, borderColor: transparentColors.white20 }} />

        {/* Copyright */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: transparentColors.white60, fontSize: "0.875rem" }}
          >
            © {currentYear} MallInHand. All rights reserved. MallInHand is a
            registered trademark of SimplyTechiefy.
          </Typography>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Link
              onClick={() => navigate("/privacy-policy")}
              sx={{
                color: transparentColors.white60,
                cursor: "pointer",
                textDecoration: "none",
                fontSize: "0.875rem",
                transition: "color 0.2s ease",
                "&:hover": { color: "#FFD700" },
              }}
            >
              Privacy Policy
            </Link>
            <Link
              onClick={() => navigate("/terms-of-service")}
              sx={{
                color: transparentColors.white60,
                cursor: "pointer",
                textDecoration: "none",
                fontSize: "0.875rem",
                transition: "color 0.2s ease",
                "&:hover": { color: "#FFD700" },
              }}
            >
              Terms of Service
            </Link>
            <Link
              onClick={() => navigate("/cookie-policy")}
              sx={{
                color: transparentColors.white60,
                cursor: "pointer",
                textDecoration: "none",
                fontSize: "0.875rem",
                transition: "color 0.2s ease",
                "&:hover": { color: "#FFD700" },
              }}
            >
              Cookie Policy
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
